import { combineEpics, Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { EpicDeps } from '../../service/StoreFactory';
import { Size } from '../../typings/common';
import { State } from '../../typings/state';
import { applySmartCenterSuccess } from '../AddCreativePage/actions';
import { CanvasAction } from '../common/actions';
import { CreativeCropApplyResponse } from '../common/requestsTypings';
import { getImageSizes } from '../CreativeImageCropHelper/CreativeImageCropHelper';

import { closeModal, init, openModal, OpenModalAction, saveModal, SaveModalAction, showError } from './actions';
import { constructSmartCenterRequestData } from './helpers';
import { SmartCenterRequestData } from './typings';

const openSmartCenterEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(openModal)),
        switchMap(
            (action: OpenModalAction): Observable<CanvasAction> => {
                const { cropValue, imageData, origSrc } = action;

                return from(getImageSizes(origSrc)).pipe(
                    map((realImageSize: Size) => {
                        return init({ ...imageData, realImageSize }, cropValue);
                    }),
                    catchError(error => of(showError(error && error.message)))
                );
            }
        )
    );
};

const applySmartCenterEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(saveModal)),
        switchMap(
            (action: SaveModalAction): Observable<CanvasAction> => {
                const {
                    canvas: {
                        creativeSmartCenterModal: { cropData, imageData }
                    },
                    sharedData
                } = store$.value.canvasEditor;

                if (!cropData || !imageData) {
                    return of(showError());
                }

                const { crop } = cropData;
                const { naturalImageSize, resourceId } = imageData;

                if (!naturalImageSize) {
                    return of(showError());
                }

                return from(constructSmartCenterRequestData(crop, naturalImageSize, action.image)).pipe(
                    switchMap((requestData: SmartCenterRequestData) => {
                        return from(
                            deps.canvasApi.addCommonCreative.smartCenterFile(
                                resourceId,
                                requestData.blob,
                                requestData.crops,
                                sharedData
                            )
                        ).pipe(
                            switchMap((response: CreativeCropApplyResponse) => [
                                closeModal(),
                                applySmartCenterSuccess(response, requestData.smartCenters)
                            ])
                        );
                    }),
                    catchError(error => of(showError(error && error.message)))
                );
            }
        )
    );
};

export const creativeSmartCenterModalWrapperEpics = combineEpics(applySmartCenterEpic, openSmartCenterEpic);
